import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import "./index.css";
import { LanguageProvider } from "./components/LanguageProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";

// Disable browser's automatic scroll restoration
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

/**
 * Root App Component wrapped with all providers
 * Extracted for reuse in both hydration and client-side rendering
 */
const RootApp = () => (
  <BrowserRouter>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <App />
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </BrowserRouter>
);

/**
 * Initialize app with hydration support for pre-rendered pages
 * 
 * Pre-rendering flow:
 * 1. During build: Post-build script generates static HTML for each route
 * 2. On page load: Browser displays pre-rendered HTML instantly (good for SEO)
 * 3. When JS loads: React hydrates the existing HTML instead of re-rendering
 * 
 * Benefits:
 * - Search engines see fully rendered HTML
 * - Faster initial page load (HTML renders before JS)
 * - Better SEO and Core Web Vitals scores
 */
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

// Check if the page was pre-rendered by looking for the explicit marker
// In development, this will always be false (no pre-rendering)
// In production, only pre-rendered pages will have this attribute set to "true"
const isPrerendered = rootElement.getAttribute('data-prerendered') === 'true';

if (isPrerendered) {
  // Hydrate pre-rendered HTML (faster, SEO-friendly)
  // This preserves the existing DOM and just attaches React event listeners
  hydrateRoot(rootElement, <RootApp />);
} else {
  // Client-side render for dynamic pages and development mode
  // This completely replaces the content of root element
  createRoot(rootElement).render(<RootApp />);
}
